import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardCheck, ArrowLeft, Users, BarChart3, AlertTriangle, Sparkles,
  TrendingUp, Target, Search, Loader2, Brain, CheckCircle2, XCircle,
  Clock, Filter, RefreshCw, Zap, HelpCircle, Trophy
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { AdminLayout } from "../../components/layout/AdminLayout";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const SEVERITY_STYLE = {
  critical: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
  success: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  info: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500" },
};

const AssessmentDetailPage = () => {
  const { assessmentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [learnerSearch, setLearnerSearch] = useState("");
  const [learnerFilter, setLearnerFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("overview");

  const variant = user?.role === "manager" ? "manager" : user?.role === "tenant_admin" ? "tenant" : "admin";
  const basePath = user?.role === "manager" ? "/manager" : user?.role === "tenant_admin" ? "/tenant" : "/admin";

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/assessments/${assessmentId}/analytics`);
      setAnalytics(res.data);
    } catch (e) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const generateAiInsights = async () => {
    if (!analytics) return;
    setAiLoading(true);
    try {
      const res = await api.post(`/assessments/${assessmentId}/ai-insights`, {
        analytics_summary: {
          title: analytics.assessment.title,
          passing_score: analytics.assessment.passing_score,
          question_count: analytics.assessment.question_count,
          total_assigned: analytics.summary.total_assigned,
          attempted: analytics.summary.attempted,
          pass_rate: analytics.summary.pass_rate,
          avg_score: analytics.summary.avg_score,
          difficult_questions: analytics.summary.difficult_questions,
          department_breakdown: analytics.department_breakdown,
        }
      });
      setAiInsights(res.data);
    } catch (e) {
      toast.error("Failed to generate AI insights");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading || !analytics) {
    return (
      <AdminLayout title="Assessment Analytics" variant={variant}>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  const { assessment, summary, learner_analytics, question_analytics, department_breakdown } = analytics;

  const filteredLearners = learner_analytics.filter(la => {
    if (learnerSearch && !la.full_name?.toLowerCase().includes(learnerSearch.toLowerCase()) && !la.email?.toLowerCase().includes(learnerSearch.toLowerCase())) return false;
    if (learnerFilter === "passed" && !la.passed) return false;
    if (learnerFilter === "failed" && (la.passed || la.attempt_count === 0)) return false;
    if (learnerFilter === "not_attempted" && la.attempt_count > 0) return false;
    return true;
  });

  const scorePieData = [
    { name: "Passed", value: summary.passed, color: "#10b981" },
    { name: "Failed", value: summary.failed, color: "#ef4444" },
    { name: "Not Attempted", value: summary.not_attempted, color: "#94a3b8" },
  ].filter(d => d.value > 0);

  const questionBarData = question_analytics.map(q => ({
    name: `Q${q.index}`, accuracy: q.accuracy, is_difficult: q.is_difficult,
  }));

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "questions", label: "Questions", icon: HelpCircle },
    { id: "learners", label: "Learners", icon: Users },
    { id: "ai", label: "AI Insights", icon: Brain },
  ];

  return (
    <AdminLayout title={assessment.title} variant={variant}>
      <div className="space-y-6" data-testid="assessment-detail-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="icon" className="mt-1 shrink-0" onClick={() => navigate(`${basePath}/assessments`)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-[10px]"><ClipboardCheck className="w-3 h-3 mr-1" /> Assessment</Badge>
                <Badge variant="outline" className="text-[10px]">{assessment.question_count} questions</Badge>
                <Badge variant="outline" className="text-[10px]"><Target className="w-3 h-3 mr-1" /> Pass: {assessment.passing_score}%</Badge>
                {assessment.time_limit_minutes > 0 && <Badge variant="outline" className="text-[10px]"><Clock className="w-3 h-3 mr-1" /> {assessment.time_limit_minutes}m</Badge>}
              </div>
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>{assessment.title}</h1>
              {assessment.description && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{assessment.description}</p>}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAnalytics} data-testid="refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          {[
            { label: "Assigned", value: summary.total_assigned, icon: Users, color: "#3b82f6", bg: "rgba(59,130,246,0.08)" },
            { label: "Attempted", value: summary.attempted, icon: ClipboardCheck, color: "#8b5cf6", bg: "rgba(139,92,246,0.08)" },
            { label: "Pass Rate", value: `${summary.pass_rate}%`, icon: Trophy, color: "#10b981", bg: "rgba(16,185,129,0.08)" },
            { label: "Avg Score", value: `${summary.avg_score}%`, icon: TrendingUp, color: "#f59e0b", bg: "rgba(245,158,11,0.08)" },
            { label: "Passed", value: summary.passed, icon: CheckCircle2, color: "#10b981", bg: "rgba(16,185,129,0.08)" },
            { label: "Hard Questions", value: summary.difficult_questions, icon: AlertTriangle, color: summary.difficult_questions > 0 ? "#ef4444" : "#94a3b8", bg: summary.difficult_questions > 0 ? "rgba(239,68,68,0.08)" : "rgba(148,163,184,0.08)" },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card className="border hover:shadow-md transition-shadow" data-testid={`summary-stat-${i}`}>
                <CardContent className="p-4">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: s.bg }}>
                    <s.icon className="w-4.5 h-4.5" style={{ color: s.color }} />
                  </div>
                  <p className="text-2xl font-bold tracking-tight">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted/50 rounded-xl w-fit" data-testid="analytics-tabs">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "overview" && (
            <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pass/Fail Pie */}
                <Card>
                  <CardContent className="p-5">
                    <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><Trophy className="w-4 h-4 text-primary" /> Result Distribution</h3>
                    <div className="flex items-center gap-6">
                      <ResponsiveContainer width={160} height={160}>
                        <PieChart>
                          <Pie data={scorePieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3} strokeWidth={0}>
                            {scorePieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                          </Pie>
                          <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-3 flex-1">
                        {scorePieData.map((d, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: d.color }} /><span className="text-sm text-muted-foreground">{d.name}</span></div>
                            <span className="text-sm font-bold">{d.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Dept Performance */}
                <Card>
                  <CardContent className="p-5">
                    <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" /> Department Performance</h3>
                    <div className="space-y-3">
                      {department_breakdown.map((dept, i) => (
                        <div key={i} className="space-y-1.5">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{dept.department}</span>
                            <span className="text-muted-foreground">{dept.pass_rate}% pass rate | Avg: {dept.avg_score}%</span>
                          </div>
                          <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-muted/50">
                            <div className="bg-emerald-500 rounded-full" style={{ width: `${(dept.passed / Math.max(dept.total, 1)) * 100}%` }} />
                            <div className="bg-red-400 rounded-full" style={{ width: `${(dept.failed / Math.max(dept.total, 1)) * 100}%` }} />
                          </div>
                          <div className="flex gap-3 text-[10px] text-muted-foreground">
                            <span>{dept.passed} passed</span><span>{dept.failed} failed</span><span>{dept.total - dept.passed - dept.failed} pending</span>
                          </div>
                        </div>
                      ))}
                      {department_breakdown.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No department data</p>}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Question Accuracy */}
              {questionBarData.length > 0 && (
                <Card>
                  <CardContent className="p-5">
                    <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><HelpCircle className="w-4 h-4 text-primary" /> Question Accuracy</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={questionBarData}>
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                        <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} formatter={(v) => [`${v}%`, "Accuracy"]} />
                        <Bar dataKey="accuracy" radius={[4, 4, 0, 0]}>
                          {questionBarData.map((entry, i) => <Cell key={i} fill={entry.is_difficult ? "#ef4444" : entry.accuracy >= 80 ? "#10b981" : "#3b82f6"} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {activeTab === "questions" && (
            <motion.div key="questions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
              {question_analytics.map((q, i) => (
                <Card key={i} className={`${q.is_difficult ? "border-red-200 bg-red-50/20" : ""}`} data-testid={`question-card-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-bold text-muted-foreground shrink-0">{q.index}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{q.text}</p>
                            {q.is_difficult && <Badge variant="destructive" className="text-[9px]"><AlertTriangle className="w-3 h-3 mr-0.5" /> Difficult</Badge>}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>{q.total_answered} answered</span>
                            <span className="text-emerald-600 font-medium">{q.correct} correct</span>
                            <span className="text-red-600 font-medium">{q.total_answered - q.correct} incorrect</span>
                          </div>
                        </div>
                      </div>
                      <div className="w-14 text-center shrink-0">
                        <div className="relative w-12 h-12 mx-auto">
                          <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                            <path d="M18 2.5a15.5 15.5 0 010 31 15.5 15.5 0 010-31" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                            <path d="M18 2.5a15.5 15.5 0 010 31 15.5 15.5 0 010-31" fill="none" stroke={q.accuracy >= 80 ? "#10b981" : q.accuracy >= 50 ? "#3b82f6" : "#ef4444"} strokeWidth="3" strokeDasharray={`${q.accuracy} ${100 - q.accuracy}`} strokeLinecap="round" />
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">{q.accuracy}%</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {question_analytics.length === 0 && <Card><CardContent className="py-12 text-center text-muted-foreground">No question data available</CardContent></Card>}
            </motion.div>
          )}

          {activeTab === "learners" && (
            <motion.div key="learners" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search learners..." value={learnerSearch} onChange={e => setLearnerSearch(e.target.value)} className="pl-10 h-9" data-testid="learner-search" />
                </div>
                <Select value={learnerFilter} onValueChange={setLearnerFilter}>
                  <SelectTrigger className="w-[160px] h-9" data-testid="learner-filter">
                    <Filter className="w-3 h-3 mr-1" /><SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ({learner_analytics.length})</SelectItem>
                    <SelectItem value="passed">Passed ({summary.passed})</SelectItem>
                    <SelectItem value="failed">Failed ({summary.failed})</SelectItem>
                    <SelectItem value="not_attempted">Not Attempted ({summary.not_attempted})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-muted">
                          <th className="text-left p-3 font-semibold text-muted-foreground">Learner</th>
                          <th className="text-left p-3 font-semibold text-muted-foreground">Department</th>
                          <th className="text-center p-3 font-semibold text-muted-foreground">Attempts</th>
                          <th className="text-center p-3 font-semibold text-muted-foreground">Best Score</th>
                          <th className="text-center p-3 font-semibold text-muted-foreground">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLearners.map((la, i) => (
                          <tr key={la.user_id} className="border-b border-muted/50 hover:bg-muted/30 transition-colors" data-testid={`learner-row-${i}`}>
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                  {la.full_name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
                                </div>
                                <div><p className="font-medium">{la.full_name}</p><p className="text-xs text-muted-foreground">{la.email}</p></div>
                              </div>
                            </td>
                            <td className="p-3 text-muted-foreground">{la.department || "-"}</td>
                            <td className="p-3 text-center font-medium">{la.attempt_count}</td>
                            <td className="p-3 text-center"><span className={`font-bold ${la.best_score >= assessment.passing_score ? "text-emerald-600" : la.best_score > 0 ? "text-red-600" : "text-muted-foreground"}`}>{la.best_score > 0 ? `${la.best_score}%` : "-"}</span></td>
                            <td className="p-3 text-center">
                              <Badge variant="outline" className={`text-[10px] ${la.passed ? "bg-emerald-50 text-emerald-700 border-emerald-200" : la.attempt_count > 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                                {la.passed ? "Passed" : la.attempt_count > 0 ? "Failed" : "Not Attempted"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredLearners.length === 0 && <div className="py-12 text-center text-muted-foreground">No learners match your filter</div>}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeTab === "ai" && (
            <motion.div key="ai" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
              {!aiInsights && !aiLoading && (
                <Card className="border-dashed border-2 border-primary/20">
                  <CardContent className="py-12 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4"><Brain className="w-8 h-8 text-primary" /></div>
                    <h3 className="text-lg font-bold mb-2">AI-Powered Assessment Analytics</h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">Analyze pass rates, question difficulty, score distributions, and get actionable recommendations.</p>
                    <Button onClick={generateAiInsights} size="lg" data-testid="generate-ai-btn"><Sparkles className="w-5 h-5 mr-2" /> Generate AI Insights</Button>
                  </CardContent>
                </Card>
              )}
              {aiLoading && <Card><CardContent className="py-16 text-center"><Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" /><p className="text-sm text-muted-foreground">Analyzing assessment data...</p></CardContent></Card>}
              {aiInsights && !aiLoading && (
                <div className="space-y-6">
                  <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border-0">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3"><Brain className="w-5 h-5 text-primary" /><h3 className="text-sm font-bold text-white/70 uppercase tracking-wider">AI Executive Summary</h3></div>
                      <p className="text-base leading-relaxed text-white/90">{aiInsights.summary}</p>
                      <Button variant="outline" size="sm" className="mt-4 text-white border-white/20 hover:bg-white/10" onClick={generateAiInsights}><RefreshCw className="w-4 h-4 mr-1" /> Regenerate</Button>
                    </CardContent>
                  </Card>
                  {aiInsights.insights?.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {aiInsights.insights.map((insight, i) => {
                        const style = SEVERITY_STYLE[insight.severity] || SEVERITY_STYLE.info;
                        return (
                          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
                            <Card className={`${style.bg} ${style.border} border h-full`} data-testid={`ai-insight-${i}`}>
                              <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                  <span className={`w-2.5 h-2.5 rounded-full ${style.dot} mt-1.5 shrink-0`} />
                                  <div>
                                    <h4 className={`text-sm font-bold ${style.text}`}>{insight.title}</h4>
                                    <p className="text-xs text-muted-foreground mt-1">{insight.description}</p>
                                    {insight.metric && <span className={`inline-block mt-2 text-xs font-bold px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>{insight.metric}</span>}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                  {aiInsights.recommendations?.length > 0 && (
                    <Card><CardContent className="p-5">
                      <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" /> AI Recommendations</h3>
                      <div className="space-y-3">
                        {aiInsights.recommendations.map((rec, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-amber-50/50 border border-amber-100">
                            <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700 shrink-0 mt-0.5">{i + 1}</span>
                            <p className="text-sm text-slate-700">{rec}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent></Card>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminLayout>
  );
};

export default AssessmentDetailPage;
